import * as Braze from "@braze/web-sdk";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import contract from "../../assets/contract.svg";
import logoCobee from "../../assets/logo-cobee.svg";
import { Logout } from "../../components/Logout";
import Button from "../../components/button";
import { Footer } from "../../components/footer";
import { FeatureFlags, useFeatureFlag } from "../../feature-flag";
import { LinkType, getLink } from "../../links";
import { useMetrics } from "../../metrics";
import { ROUTES } from "../../routes";
import { useAcceptAgreements } from "./useAcceptAgreements";
import { useAcceptAgreementsV2 } from "./useAcceptAgreementsV2";
import { useSelfUrl } from "./useSelfUrl";
import { useUpdateCommercialCommunications } from "./useUpdateCommercialCommunications";
import { useUpdateTermsAndConditions } from "./useUpdateTermsAndConditions";

enum BrazeGroupTypeIds {
  CommercialCommunications = "2fb10ebb-c8fb-4c9f-b2ba-3a47fc14d91a",
  TransactionalCommunications = "4946d46f-7e4a-41a5-a8d6-afadd07ce7db",
}

const Agreements = () => {
  const [novationContractAccepted, setNovationContractAccepted] =
    useState(false);
  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] =
    useState(false);
  const [
    commercialCommunicationsAccepted,
    setCommercialCommunicationsAccepted,
  ] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { trackAction, pageView, trackEvent } = useMetrics();

  const navigate = useNavigate();
  const { acceptAgreements } = useAcceptAgreements();
  const { updateCommercialCommunications } =
    useUpdateCommercialCommunications();
  const { updateTermsAndConditions } = useUpdateTermsAndConditions();
  const { acceptAgreements: acceptAgreementsV2 } = useAcceptAgreementsV2();

  const { t, i18n } = useTranslation();
  const selfUrl = useSelfUrl();
  const { isEnabled: isAgreementsShowTicksAccordingly } = useFeatureFlag(
    FeatureFlags.EG_4656
  );
  const { state } = useLocation();
  const showCommercialCommunications = state?.showCommercialCommunications;
  const showTermsAndConditions = state?.showTermsAndConditions;
  const showNovationContract = state?.showNovationContract;

  const handlerNovationContract = () => {
    trackAction.click({
      screenName: "Agreements",
      componentName: "checkbox novationContract",
      additionalProperties: {
        checked: !novationContractAccepted,
      },
    });
    setNovationContractAccepted(true);
    window.open(`${selfUrl}${ROUTES.UNSIGNED_NOVATION_CONTRACT}`);
  };

  const handlerTermsAndCondition = () => {
    trackAction.click({
      screenName: "Agreements",
      componentName: "checkbox termsAndConditions",
      additionalProperties: {
        checked: !termsAndConditionsAccepted,
      },
    });
    setTermsAndConditionsAccepted(!termsAndConditionsAccepted);
  };

  const handlerOnClick = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackAction.click({
      screenName: "Agreements",
      componentName: "button finish",
    });

    if (isAgreementsShowTicksAccordingly) {
      trackEvent("Agreements Submit Button Clicked", {
        showCommercialCommunications,
        showTermsAndConditions,
        showNovationContract,
        novationContractAccepted,
        termsAndConditionsAccepted,
        commercialCommunicationsAccepted,
      });
      if (showCommercialCommunications) {
        await updateCommercialCommunications(commercialCommunicationsAccepted);
      }
      if (showTermsAndConditions) {
        await updateTermsAndConditions();
      }
      if (showNovationContract) {
        await acceptAgreementsV2();
      }
      navigate(ROUTES.HOME);

      return;
    }

    if (!(novationContractAccepted && termsAndConditionsAccepted)) {
      trackAction.showed({
        screenName: "Agreements",
        componentName: "error hasError",
        additionalProperties: {
          error: "You have to accept all agreements",
          novationContractAccepted: novationContractAccepted,
          termsAndConditionsAccepted: termsAndConditionsAccepted,
        },
      });
      setHasError(true);
      return;
    }

    if (!commercialCommunicationsAccepted) {
      Braze.getUser()?.addToSubscriptionGroup(
        BrazeGroupTypeIds.CommercialCommunications
      );
    }

    Braze.getUser()?.addToSubscriptionGroup(
      BrazeGroupTypeIds.TransactionalCommunications
    );

    trackAction.click({
      screenName: "Agreements",
      componentName: "Commercial Communications",
      additionalProperties: {
        checked: commercialCommunicationsAccepted,
      },
    });

    try {
      await acceptAgreements();
      navigate(ROUTES.HOME);
    } catch (error) {
      trackAction.showed({
        screenName: "Agreements",
        componentName: "error hasAcceptRequestError",
        additionalProperties: {
          error: "Error accepting agreements",
        },
      });
      navigate(ROUTES.PAGE_FAILED, {
        state: {
          title: t("page-failed.agreements.title"),
          errorPage: "AgreementsFailed",
        },
      });
    }
  };

  const handleOnClickLinkPrivacyPolicy = () => {
    trackAction.click({
      screenName: "Agreements",
      componentName: "link privacyPolicy",
    });
  };

  const handleOnClickLinkTermsAndConditions = () => {
    trackAction.click({
      screenName: "Agreements",
      componentName: "link termsAndConditions",
    });
  };

  const handleOnClickLinkNovationContract = () => {
    trackAction.click({
      screenName: "Agreements",
      componentName: "link novationContract",
    });
  };

  const isButtonDisabled = useCallback(() => {
    if (!isAgreementsShowTicksAccordingly) {
      return false;
    }

    if (showTermsAndConditions && showNovationContract) {
      return !termsAndConditionsAccepted || !novationContractAccepted;
    }

    if (showTermsAndConditions && !showNovationContract) {
      return !termsAndConditionsAccepted;
    }
  }, [
    termsAndConditionsAccepted,
    novationContractAccepted,
    showTermsAndConditions,
    showNovationContract,
    isAgreementsShowTicksAccordingly,
  ]);

  useEffect(() => {
    pageView("Agreements");
  }, []);

  useEffect(() => {
    if (termsAndConditionsAccepted && novationContractAccepted) {
      setHasError(false);
    }
  }, [termsAndConditionsAccepted, novationContractAccepted]);

  return (
    <div className="flex flex-col items-center h-full">
      <header className="grid grid-cols-12 mt-4 h-16 items-center w-4/5">
        <div className="flex justify-center col-start-3 col-span-8">
          <img src={logoCobee} alt="Logo de Cobee" className="block" />
        </div>
        <div className="col-span-1 items-start w-max">
          <Logout />
        </div>
      </header>
      <div className="flex justify-center mt-10">
        <h1 className="text-3xl text-gray-900 text-center w-60">
          {t("agreements.title")}
        </h1>
      </div>
      <div className="flex justify-center mt-1">
        <p className="text-sm text-gray-700 text-center w-60">
          {t("agreements.subtitle")}
        </p>
      </div>

      <div className="flex justify-center">
        <img src={contract} alt={t("agreements.image.altDescription")} />
      </div>
      <div className="flex justify-center mx-4">
        <form>
          {!isAgreementsShowTicksAccordingly ? (
            <div className="flex flex-col">
              <div className="inline-flex gap-2 items-center">
                <input
                  id="novation-contract"
                  className="w-4 h-4 border-2 rounded-sm accent-purple-500"
                  type="checkbox"
                  checked={novationContractAccepted}
                  onChange={handlerNovationContract}
                />
                <label htmlFor="novation-contract" className="text-sm">
                  <Trans i18nKey={"agreements.novation-contract.title"}>
                    <a
                      className="text-secondary-main"
                      href={`${selfUrl}${ROUTES.UNSIGNED_NOVATION_CONTRACT}`}
                      target="_blank"
                      onClick={handleOnClickLinkNovationContract}
                    ></a>
                  </Trans>
                  <label className="text-sm text-error-dark">*</label>
                </label>
              </div>
              <label className="text-sm text-gray-700 ml-6">
                {t("agreements.novation-contract.explanation")}
              </label>
            </div>
          ) : showNovationContract ? (
            <div className="flex flex-col">
              <div className="inline-flex gap-2 items-center">
                <input
                  id="novation-contract"
                  className="w-4 h-4 border-2 rounded-sm accent-purple-500"
                  type="checkbox"
                  checked={novationContractAccepted}
                  onChange={handlerNovationContract}
                />
                <label htmlFor="novation-contract" className="text-sm">
                  <Trans i18nKey={"agreements.novation-contract.title"}>
                    <a
                      className="text-secondary-main"
                      href={`${selfUrl}${ROUTES.UNSIGNED_NOVATION_CONTRACT}`}
                      target="_blank"
                      onClick={handleOnClickLinkNovationContract}
                    ></a>
                  </Trans>
                  <label className="text-sm text-error-dark">*</label>
                </label>
              </div>
              <label className="text-sm text-gray-700 ml-6">
                {t("agreements.novation-contract.explanation")}
              </label>
            </div>
          ) : null}
          {!isAgreementsShowTicksAccordingly ? (
            <div className="flex">
              <div className="inline-flex gap-2 items-center">
                <input
                  id="terms-and-conditions"
                  className="w-4 h-4 border-2 rounded-sm accent-purple-500"
                  type="checkbox"
                  checked={termsAndConditionsAccepted}
                  onChange={handlerTermsAndCondition}
                />
                <label htmlFor="terms-and-conditions" className="text-sm">
                  <Trans i18nKey={"agreements.legal-terms"}>
                    <a
                      className="text-secondary-main"
                      href={getLink({
                        linkType: LinkType.TermsAndConditions,
                        language: i18n.language,
                      })}
                      target="_blank"
                      onClick={handleOnClickLinkTermsAndConditions}
                    ></a>
                    <a
                      className="text-secondary-main"
                      href={getLink({
                        linkType: LinkType.PrivacyPolicy,
                        language: i18n.language,
                      })}
                      target="_blank"
                      onClick={handleOnClickLinkPrivacyPolicy}
                    ></a>
                  </Trans>
                </label>
              </div>
            </div>
          ) : showTermsAndConditions ? (
            <div className="flex">
              <div className="inline-flex gap-2 items-center">
                <input
                  id="terms-and-conditions"
                  className="w-4 h-4 border-2 rounded-sm accent-purple-500"
                  type="checkbox"
                  checked={termsAndConditionsAccepted}
                  onChange={handlerTermsAndCondition}
                />
                <label htmlFor="terms-and-conditions" className="text-sm">
                  <Trans i18nKey={"agreements.legal-terms"}>
                    <a
                      className="text-secondary-main"
                      href={getLink({
                        linkType: LinkType.TermsAndConditions,
                        language: i18n.language,
                      })}
                      target="_blank"
                      onClick={handleOnClickLinkTermsAndConditions}
                    ></a>
                    <a
                      className="text-secondary-main"
                      href={getLink({
                        linkType: LinkType.PrivacyPolicy,
                        language: i18n.language,
                      })}
                      target="_blank"
                      onClick={handleOnClickLinkPrivacyPolicy}
                    ></a>
                  </Trans>
                </label>
              </div>
            </div>
          ) : null}
          {!isAgreementsShowTicksAccordingly ? (
            <div className="flex">
              <div className="inline-flex gap-2 items-center">
                <input
                  id="commercial-communications"
                  className="w-4 h-4 border-2 rounded-sm accent-purple-500"
                  type="checkbox"
                  checked={commercialCommunicationsAccepted}
                  onChange={() =>
                    setCommercialCommunicationsAccepted(
                      !commercialCommunicationsAccepted
                    )
                  }
                />
                <label htmlFor="commercial-communications" className="text-sm">
                  {t("agreements.accept-commercial-communications")}
                </label>
              </div>
            </div>
          ) : showCommercialCommunications ? (
            <div className="flex">
              <div className="inline-flex gap-2 items-center">
                <input
                  id="commercial-communications"
                  className="w-4 h-4 border-2 rounded-sm accent-purple-500"
                  type="checkbox"
                  checked={commercialCommunicationsAccepted}
                  onChange={() =>
                    setCommercialCommunicationsAccepted(
                      !commercialCommunicationsAccepted
                    )
                  }
                />
                <label htmlFor="commercial-communications" className="text-sm">
                  {t("agreements.accept-commercial-communications")}
                </label>
              </div>
            </div>
          ) : null}
          {hasError && (
            <div className="flex justify-center mt-2 text-error-dark">
              <p>{t("agreements.error.requiredFields")}</p>
            </div>
          )}
          <div className="flex justify-center mt-8">
            <div className="flex justify-center">
              <Button
                onClick={handlerOnClick}
                text={t("agreements.finish.button")}
                disabled={isButtonDisabled()}
              />
            </div>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export { Agreements };
